import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { ToastPill } from 'components/toasts/ToastPill';
import { useAssignmentsQuery } from 'modules/assignments/query';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { apiErrorMessage, useToast } from '../hooks';
import {
  useAvailabilityExceptionsQuery,
  useAvailabilityQuery,
  useDeleteExceptionMutation,
  useDeleteSemesterBreakMutation,
  useSemesterBreaksQuery
} from '../query';
import { AvailabilityException, SemesterBreak } from '../types';
import {
  formatMinutesAsHours,
  formatRange,
  toFullWeek,
  totalWeekMinutes,
  upcomingIsoWeeks,
  weekdayLabel
} from '../utils';
import { ExceptionEditorDialog } from './ExceptionEditorDialog';
import { HoursRequestSection } from './HoursRequestSection';
import { SemesterBreakDialog } from './SemesterBreakDialog';
import { TemplateEditorDialog } from './TemplateEditorDialog';

const formatIsoDate = (isoDate: string): string =>
  format(new Date(isoDate), 'dd.MM.yyyy');

const exceptionWeekLabel = (exception: AvailabilityException): string => {
  const week = upcomingIsoWeeks(1)[0];
  const isCurrent =
    week.year === exception.year &&
    week.calendarWeek === exception.calendarWeek;
  return `KW ${exception.calendarWeek}/${exception.year}${
    isCurrent ? ` · ${getTrans('availabilityPage.exceptions.thisWeek')}` : ''
  }`;
};

interface ConfirmState {
  message: string;
  onConfirm: () => void;
}

/** "My Availability": weekly template, week exceptions and semester breaks. */
export const Availability = () => {
  const availabilityQuery = useAvailabilityQuery();
  const exceptionsQuery = useAvailabilityExceptionsQuery();
  const { isEnabled: isSemesterBreakEnabled } = useFeatureToggle(
    FeatureFlag.PRO_SEMESTER_BREAK
  );
  const breaksQuery = useSemesterBreaksQuery(isSemesterBreakEnabled);
  const assignmentsQuery = useAssignmentsQuery();

  const deleteExceptionMutation = useDeleteExceptionMutation();
  const deleteBreakMutation = useDeleteSemesterBreakMutation();

  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [exceptionEditor, setExceptionEditor] = useState<{
    open: boolean;
    initialWeek?: { year: number; calendarWeek: number };
  }>({ open: false });
  const [breakEditor, setBreakEditor] = useState<{
    open: boolean;
    existing: SemesterBreak | null;
  }>({ open: false, existing: null });
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { toastMessage, showToast } = useToast();

  if (availabilityQuery.isLoading || exceptionsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
        <CircularProgress data-testid="availability-loading" />
      </Box>
    );
  }

  // A failed list is not an empty one: editing from it would build on what the talent cannot see.
  const failed = [
    availabilityQuery,
    exceptionsQuery,
    ...(isSemesterBreakEnabled ? [breaksQuery] : [])
  ].find(query => query.isError);
  if (failed) {
    return (
      <Alert severity="error" data-testid="availability-error">
        {apiErrorMessage(failed.error)}
      </Alert>
    );
  }

  const templateDays = toFullWeek(availabilityQuery.data?.data.days ?? []);
  const hasTemplate = (availabilityQuery.data?.data.days ?? []).length > 0;
  const weeklyMinutes = totalWeekMinutes(templateDays);
  const exceptions = exceptionsQuery.data?.data ?? [];
  const semesterBreaks = breaksQuery.data?.data ?? [];
  const assignments = assignmentsQuery.data?.data ?? [];

  const handleDeleteException = (exception: AvailabilityException) =>
    setConfirm({
      message: getTrans('availabilityPage.exceptions.deleteConfirm'),
      onConfirm: () =>
        deleteExceptionMutation.mutate(exception.refId, {
          onSuccess: () =>
            showToast(getTrans('availabilityPage.exceptions.deletedToast')),
          onError: responseError =>
            setActionError(apiErrorMessage(responseError))
        })
    });

  const handleDeleteBreak = (semesterBreak: SemesterBreak) =>
    setConfirm({
      message: getTrans('availabilityPage.breaks.deleteConfirm'),
      onConfirm: () =>
        deleteBreakMutation.mutate(semesterBreak.refId, {
          onSuccess: () =>
            showToast(getTrans('availabilityPage.breaks.deletedToast')),
          onError: responseError =>
            setActionError(apiErrorMessage(responseError))
        })
    });

  return (
    <Box data-testid="availability-content">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getTrans('availabilityPage.intro')}
      </Typography>

      {actionError && (
        <Alert
          severity="error"
          onClose={() => setActionError(null)}
          sx={{ mb: 2 }}
          data-testid="availability-action-error"
        >
          {actionError}
        </Alert>
      )}

      {/* Weekly template */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {getTrans('availabilityPage.template.title')}
            </Typography>
            <Button
              size="small"
              onClick={() => setTemplateEditorOpen(true)}
              data-testid="edit-template-button"
            >
              {getTrans(
                hasTemplate ? 'general.edit' : 'availabilityPage.template.set'
              )}
            </Button>
          </Box>

          {hasTemplate ? (
            <>
              {templateDays.map(day => (
                <Box
                  key={day.dayOfWeek}
                  sx={{ display: 'flex', mb: 0.5 }}
                  data-testid={`template-day-${day.dayOfWeek}`}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, minWidth: 96 }}
                  >
                    {weekdayLabel(day.dayOfWeek)}
                  </Typography>
                  <Box>
                    <Typography
                      variant="body2"
                      color={
                        day.available.length ? 'text.primary' : 'text.disabled'
                      }
                    >
                      {day.available.length
                        ? day.available.map(formatRange).join(', ')
                        : getTrans('availabilityPage.day.unavailable')}
                    </Typography>
                    {day.preferred.length > 0 && (
                      <Typography variant="caption" color="primary">
                        {getTrans('availabilityPage.day.preferredPrefix')}{' '}
                        {day.preferred.map(formatRange).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {getTransWithParams('availabilityPage.template.totalHours', {
                  hours: formatMinutesAsHours(weeklyMinutes)
                })}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {getTrans('availabilityPage.template.empty')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Week exceptions */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {getTrans('availabilityPage.exceptions.title')}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setExceptionEditor({ open: true })}
              data-testid="add-exception-button"
            >
              {getTrans('availabilityPage.exceptions.add')}
            </Button>
          </Box>

          {exceptions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {getTrans('availabilityPage.exceptions.empty')}
            </Typography>
          )}

          {exceptions.map(exception => (
            <Box
              key={exception.refId}
              sx={{ mb: 1.5 }}
              data-testid={`exception-${exception.year}-${exception.calendarWeek}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {exceptionWeekLabel(exception)}
                </Typography>
                {exception.wholeWeekUnavailable ? (
                  <Chip
                    size="small"
                    color="warning"
                    label={getTrans('availabilityPage.exceptions.wholeWeekOff')}
                  />
                ) : (
                  <Chip
                    size="small"
                    label={getTransWithParams(
                      'availabilityPage.exceptions.daysChanged',
                      { count: exception.days?.length ?? 0 }
                    )}
                  />
                )}
              </Box>
              {exception.note && (
                <Typography variant="caption" color="text.secondary">
                  „{exception.note}“
                </Typography>
              )}
              <Box>
                <Button
                  size="small"
                  onClick={() =>
                    setExceptionEditor({
                      open: true,
                      initialWeek: {
                        year: exception.year,
                        calendarWeek: exception.calendarWeek
                      }
                    })
                  }
                >
                  {getTrans('general.edit')}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteException(exception)}
                  data-testid={`delete-exception-${exception.calendarWeek}`}
                >
                  {getTrans('general.delete')}
                </Button>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Semester breaks */}
      {isSemesterBreakEnabled && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {getTrans('availabilityPage.breaks.title')}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setBreakEditor({ open: true, existing: null })}
                data-testid="add-break-button"
              >
                {getTrans('availabilityPage.breaks.add')}
              </Button>
            </Box>

            {semesterBreaks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {getTrans('availabilityPage.breaks.empty')}
              </Typography>
            )}

            {semesterBreaks.map(semesterBreak => (
              <Box
                key={semesterBreak.refId}
                sx={{ mb: 2 }}
                data-testid={`break-${semesterBreak.refId}`}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatIsoDate(semesterBreak.startDate)} –{' '}
                  {formatIsoDate(semesterBreak.endDate)}
                </Typography>
                {semesterBreak.note && (
                  <Typography variant="caption" color="text.secondary">
                    „{semesterBreak.note}“
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {getTransWithParams(
                    'availabilityPage.breaks.patternSummary',
                    {
                      days: semesterBreak.days.length
                    }
                  )}
                </Typography>
                <Box>
                  <Button
                    size="small"
                    onClick={() =>
                      setBreakEditor({ open: true, existing: semesterBreak })
                    }
                  >
                    {getTrans('general.edit')}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteBreak(semesterBreak)}
                  >
                    {getTrans('general.delete')}
                  </Button>
                </Box>
                <HoursRequestSection
                  semesterBreak={semesterBreak}
                  assignments={assignments}
                  onChanged={showToast}
                />
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      <Typography variant="caption" color="text.secondary">
        {getTrans('availabilityPage.footerHint')}
      </Typography>

      <TemplateEditorDialog
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        initialDays={templateDays}
        onSaved={() =>
          showToast(getTrans('availabilityPage.template.savedToast'))
        }
      />
      <ExceptionEditorDialog
        open={exceptionEditor.open}
        onClose={() => setExceptionEditor({ open: false })}
        exceptions={exceptions}
        templateDays={templateDays}
        initialWeek={exceptionEditor.initialWeek}
        onSaved={() =>
          showToast(getTrans('availabilityPage.exceptions.savedToast'))
        }
      />
      <SemesterBreakDialog
        open={breakEditor.open}
        onClose={() => setBreakEditor({ open: false, existing: null })}
        existing={breakEditor.existing}
        templateDays={templateDays}
        onSaved={() =>
          showToast(getTrans('availabilityPage.breaks.savedToast'))
        }
      />

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{getTrans('general.confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>
            {getTrans('general.cancel')}
          </Button>
          <Button
            color="error"
            data-testid="confirm-delete"
            onClick={() => {
              confirm?.onConfirm();
              setConfirm(null);
            }}
          >
            {getTrans('general.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastPill open={!!toastMessage} message={toastMessage ?? ''} />
    </Box>
  );
};
